/*
Copyright (c) 2012-2026 Grab Taxi Holdings PTE LTD (GRAB), All Rights Reserved.
NOTICE: All information contained herein is, and remains the property of GRAB.
The intellectual and technical concepts contained herein are confidential,
proprietary and controlled by GRAB and may be covered by patents, patents in process,
and are protected by trade secret or copyright law.

You are strictly forbidden to copy, download, store (in any medium), transmit, disseminate,
adapt or change this material in any way unless prior written permission is obtained from GRAB.
Access to the source code contained herein is hereby forbidden to anyone except current GRAB
employees or contractors with binding Confidentiality and Non-disclosure agreements explicitly
covering such access.

The copyright notice above does not evidence any actual or intended publication or disclosure of
this source code, which includes information that is confidential and/or proprietary,
and is a trade secret, of GRAB.

ANY REPRODUCTION, MODIFICATION, DISTRIBUTION, PUBLIC PERFORMANCE, OR PUBLIC DISPLAY OF OR
THROUGH USE OF THIS SOURCE CODE WITHOUT THE EXPRESS WRITTEN CONSENT OF GRAB IS STRICTLY PROHIBITED,
AND IN VIOLATION OF APPLICABLE LAWS AND INTERNATIONAL TREATIES. THE RECEIPT OR POSSESSION OF THIS
SOURCE CODE AND/OR RELATED INFORMATION DOES NOT CONVEY OR IMPLY ANY RIGHTS TO REPRODUCE,
DISCLOSE OR DISTRIBUTE ITS CONTENTS, OR TO MANUFACTURE, USE, OR SELL ANYTHING THAT IT MAY DESCRIBE,
IN WHOLE OR IN PART.
*/
/* eslint-disable consistent-return */
import {
  OrderStateBackend,
  OrderDeliveryStateBackend,
  GeAllocationStatus,
  DeliveryStatus,
} from "order-manager/constants/orderState"
import notification from "components/common/notification"
import { OrderEtaMap } from "order-manager/types/models/order_eta"
import { isUndefined } from "lodash"
import { Order, Booking } from "order-manager/types/models/order"
import { storageHelper } from "utils/storage"
import { helpCenterJwtHandover } from "troy-common/utils/HelpCenter"
import { sessionPause } from "troy-common/middleware/session-middleware"
import { SESSION_DURATION } from "order-manager/constants/time"
import { TroyWebTracker } from "utils/tracking"
import { EventAction, EventCategory } from "order-manager/constants/tracking"
import { ORDER_MANAGER_URL_PREFIX } from "constants/routes"
import currency from "currency.js"
import { getCurrencyInfo } from "utils/currency"
import {
  CurrencySymbolLocation,
  CURRENCY_SYMBOL_LOCATION_MAP,
} from "order-manager/constants/rule"
import { ViewMode } from "order-manager/constants/viewMode"
import { convertDateTZ } from "utils/date_formatter"

let handOverInProgress = false

interface getDriverStateParams {
  order: Order | undefined
  orderPreparingEta: OrderEtaMap
  orderReadyEta: OrderEtaMap
  isFromOrderDetail: boolean
  textMap: Record<string, string>
  booking?: Booking | undefined
}

export const getDriverState = ({
  order,
  orderPreparingEta,
  orderReadyEta,
  isFromOrderDetail = false,
  textMap,
  booking,
}: getDriverStateParams): string => {
  let result = ""

  const { state, deliveryTaskpoolStatus } =
    (order?.flags?.isGrabInitiatedSplitOrder && booking ? booking : order) || {}

  if (
    state === OrderStateBackend.OrderInPrepare &&
    deliveryTaskpoolStatus === OrderDeliveryStateBackend.driverAtStore
  ) {
    result = textMap.arrived
    return result
  }

  if (
    state === OrderStateBackend.OrderInPrepare &&
    deliveryTaskpoolStatus === OrderDeliveryStateBackend.pickingUp
  ) {
    let selectedEta = {} as OrderEtaMap
    if (!order?.times.readyAt) {
      selectedEta = orderPreparingEta
    } else {
      selectedEta = orderReadyEta
    }

    const orderID = order?.orderID || ""
    if (isUndefined(selectedEta[orderID])) {
      return "-"
    }

    const etaInMinute = Math.ceil(selectedEta[orderID]?.eta / 60)
    result = textMap.minutesleft.replace(/<x>/i, etaInMinute.toString())

    return result
  }

  if (
    (state === OrderStateBackend.OrderExecuting &&
      deliveryTaskpoolStatus === OrderDeliveryStateBackend.foodCollected) ||
    (state === OrderStateBackend.OrderExecuting &&
      deliveryTaskpoolStatus === OrderDeliveryStateBackend.driverArrived)
  ) {
    result = textMap.outForDelivery
    return result
  }

  if (
    state === OrderStateBackend.OrderInPrepare &&
    deliveryTaskpoolStatus === OrderDeliveryStateBackend.allocating
  ) {
    result = textMap.findingDriver
    return result
  }

  if (state === OrderStateBackend.completed) {
    result = !isFromOrderDetail ? textMap.completed : textMap.delivered
    return result
  }

  if (
    state === OrderStateBackend.failed ||
    state === OrderStateBackend.cancelled
  ) {
    result = textMap.cancelled
    return result
  }

  return result
}

export const copyText = async (text: string): Promise<void> => {
  try {
    await navigator.clipboard.writeText(text)
    notification.success({
      message: "Copied to clipboard",
    })
  } catch (error) {
    // Fallback for when document is not focused or clipboard API fails
    const textArea = document.createElement("textarea")
    textArea.value = text
    textArea.style.position = "fixed"
    textArea.style.left = "-9999px"
    textArea.style.top = "-9999px"
    document.body.appendChild(textArea)
    textArea.focus()
    textArea.select()

    try {
      const successful = document.execCommand("copy")
      if (successful) {
        notification.success({
          message: "Copied to clipboard",
        })
      } else {
        TroyWebTracker.sendEvent({
          category: EventCategory.orderDetail,
          action: EventAction.copyToClipboardFailed,
          labels: {
            ERROR: "execCommand returned false",
          },
        })
        notification.error({
          message: "Failed to copy to clipboard",
        })
      }
    } catch (fallbackError) {
      TroyWebTracker.sendEvent({
        category: EventCategory.orderDetail,
        action: EventAction.copyToClipboardFailed,
        labels: {
          ERROR: fallbackError,
        },
      })
      notification.error({
        message: "Failed to copy to clipboard",
      })
    } finally {
      document.body.removeChild(textArea)
    }
  }
}

export const isInOrderListPage = (): boolean => {
  const pattern = new RegExp(
    `${ORDER_MANAGER_URL_PREFIX}/(.*)/(ongoing|preparing|ready|upcoming|delivering)`,
    "g",
  )
  return pattern.test(window.location.pathname)
}

export const isInOrderPage = (): boolean => {
  const pattern = new RegExp(`${ORDER_MANAGER_URL_PREFIX}(.*)`, "g")
  return pattern.test(window.location.pathname)
}

// gePreparingStatusByGeAllocation, preparing has many variation to display
// it could be this function return or order estimation get from orders-eta endpoint
export const gePreparingStatusByGeAllocation = (
  geAllocationStatus: number,
): string => {
  if (geAllocationStatus === GeAllocationStatus.AllocatingStatus) {
    return "Looking for a driver."
  }

  if (geAllocationStatus === GeAllocationStatus.GeNotFoundStatus) {
    return "Couldn't find a driver. Request again to retry."
  }

  return ""
}

export const getPreparingStatus = (
  deliveryStatus: string,
  viewMode: ViewMode,
  textMap: Record<"findingDriver" | "arrived" | "minutesleft", string>,
): string => {
  if (deliveryStatus === OrderDeliveryStateBackend.allocating) {
    return textMap.findingDriver
  }

  if (
    deliveryStatus === OrderDeliveryStateBackend.driverAtStore &&
    viewMode === ViewMode.Card
  ) {
    return textMap.arrived
  }

  // special requirement for table view
  // https://grab.slack.com/archives/C02ECA40BAN/p1660118355531469?thread_ts=1656662598.363989&cid=C02ECA40BAN
  if (
    deliveryStatus === OrderDeliveryStateBackend.driverAtStore &&
    viewMode === ViewMode.Table
  ) {
    return textMap.minutesleft.replace(/<x>/i, "0")
  }

  if (deliveryStatus === OrderDeliveryStateBackend.failed) {
    return "Couldn't find a driver. Request again to retry."
  }

  return ""
}

export const batchStringArray = (
  stringArray: string[],
  maxPerBatch: number,
): string[][] => {
  const result: string[][] = []
  let tempArray: string[] = []
  let arraySize: number = 0

  stringArray.map((stringVariable, index) => {
    if (arraySize === maxPerBatch) {
      result.push(tempArray)
      arraySize = 0
      tempArray = []
    }

    if (index === stringArray.length - 1) {
      result.push(tempArray)
    }

    tempArray.push(stringVariable)
    arraySize += 1

    return tempArray
  })

  return result
}

export const openOrderManagerHelpCenter = () => {
  const url = storageHelper.getTroyOrderManagerHelpCenterUrl()
  window.open(url, "_blank")
}

export const openOrderManagerHelpCenterUsingJWT = () => {
  const enabled = storageHelper.getToggleFeature()
    ?.enableHelpCenterTokenHandover
  if (!enabled) return openOrderManagerHelpCenter()
  if (handOverInProgress) return

  handOverInProgress = true
  helpCenterJwtHandover()
    .then(() => {
      sessionPause(SESSION_DURATION)
    })
    .catch(error => {
      TroyWebTracker.sendEvent({
        category: EventCategory.orderDetail,
        action: EventAction.hcHandoverFailed,
        labels: error,
      })
    })
    .finally(() => {
      openOrderManagerHelpCenter()
      handOverInProgress = false
    })
}

export const isOrderCancelled = (deliveryStatus: string): boolean => {
  return (
    deliveryStatus === DeliveryStatus.CANCELLED ||
    deliveryStatus === DeliveryStatus.CANCELLED_OPERATOR ||
    deliveryStatus === DeliveryStatus.CANCELLED_PASSENGER ||
    deliveryStatus === DeliveryStatus.CANCELLED_MAX ||
    deliveryStatus === DeliveryStatus.CANCELLED_DRIVER ||
    deliveryStatus === DeliveryStatus.FAILED
  )
}

export const isOrderCancelledV2 = (order?: Order): boolean => {
  // for GIS, order state != deldeliveryTaskpoolStatus since its possible for partially completed orders to exist
  if (order?.flags?.isGrabInitiatedSplitOrder)
    return isOrderCancelled(order?.state || "")

  return isOrderCancelled(order?.deliveryTaskpoolStatus || "")
}

export const isOrderInDelivery = (deliveryStatus: string) => {
  return (
    deliveryStatus === DeliveryStatus.FOOD_COLLECTED ||
    deliveryStatus === DeliveryStatus.ORDER_EXECUTING
  )
}

export const isOrderCancelledByCheckTime = (
  order: Order | undefined,
): boolean => {
  return Boolean(order?.times?.cancelledAt)
}

export const getCurrencySymbolLocation = (): CurrencySymbolLocation => {
  // @ts-ignore
  return CURRENCY_SYMBOL_LOCATION_MAP[storageHelper.getUserCountry() || "SG"]
}

export const getPriceDisplayWithCurrencySymbol = (
  priceDisplay: string,
  currencySymbol: string,
) => {
  return getCurrencySymbolLocation() === CurrencySymbolLocation.BEFORE
    ? `${currencySymbol}${priceDisplay}`
    : `${priceDisplay}${currencySymbol}`
}

export const getSubTotal = (
  currencyCode: string,
  subtotalIncludeMerchantCharge: string,
  revampedSubtotalDisplay: string,
): string => {
  if (!subtotalIncludeMerchantCharge || !revampedSubtotalDisplay) {
    return ""
  }

  const currencyInfo = getCurrencyInfo(currencyCode)
  let subTotal = 0

  // eslint-disable-next-line new-cap
  const subTotalIncludeMerchantChargeInCurrency = new currency(
    subtotalIncludeMerchantCharge,
    {
      symbol: currencyInfo.symbol,
      separator: currencyInfo.separator,
      decimal: currencyInfo.decimalSymbol,
      precision: currencyInfo.decimal,
    },
  )

  // eslint-disable-next-line new-cap
  const revampedSubtotalInCurrency = new currency(revampedSubtotalDisplay, {
    symbol: currencyInfo.symbol,
    separator: currencyInfo.separator,
    decimal: currencyInfo.decimalSymbol,
    precision: currencyInfo.decimal,
  })

  if (revampedSubtotalInCurrency.value < 0) {
    subTotal =
      subTotalIncludeMerchantChargeInCurrency.value +
      revampedSubtotalInCurrency.value
  } else {
    subTotal = revampedSubtotalInCurrency.value
  }

  // eslint-disable-next-line new-cap
  const subTotalInCurrency = new currency(subTotal, {
    symbol: currencyInfo.symbol,
    separator: currencyInfo.separator,
    decimal: currencyInfo.decimalSymbol,
    precision: 2,
    pattern: "#",
  })

  return subTotalInCurrency.format()
}

export const countOrderNotOpened = (
  orders: Order[] | undefined,
  openedList: { [key: string]: boolean },
): number => {
  let result = 0

  if (!orders) {
    return result
  }

  orders.forEach(order => {
    if (!order.labels?.isRead && !openedList[order.orderID]) {
      result += 1
    }
  })

  return result
}

export const getShortOrderId = (order: Order | undefined) => {
  if (!order) return ""
  const displayId = order.displayID
  const date = order?.times?.createdAt
    ? convertDateTZ(order.times.createdAt).format("YYYYMMDD")
    : ""

  return `${date ? `${date}-` : ""}${displayId}`
}

interface getSubOrderBookingParams {
  order?: Order
  shortOrderID?: string
}

export const getSubOrderBooking = ({
  order,
  shortOrderID,
}: getSubOrderBookingParams): Booking | undefined => {
  return order?.orderBookings?.find(b => b.shortOrderID === shortOrderID)
}
